import { useMemo } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'

// 5 levels of ant expense severity
const ANT_LEVELS = [
  { maxRatio: 0.05, size: 'text-xl', compactSize: 'text-lg', message: { es: 'Pocos', en: 'Low' } },
  { maxRatio: 0.10, size: 'text-2xl', compactSize: 'text-xl', message: { es: 'Creciendo', en: 'Growing' } },
  { maxRatio: 0.20, size: 'text-4xl', compactSize: 'text-2xl', message: { es: 'Alerta', en: 'Alert' } },
  { maxRatio: 0.35, size: 'text-5xl', compactSize: 'text-3xl', message: { es: '¡Cuidado!', en: 'Warning!' } },
  { maxRatio: 1.00, size: 'text-6xl', compactSize: 'text-4xl', message: { es: '¡Emergencia!', en: 'Emergency!' } },
]

export default function AntIndicator({ antExpenseAmount, totalExpenses, onClick, compact = false }) {
  const { locale, formatCurrency } = useLanguage()
  const isSpanish = locale === 'es-MX'

  const { level, ratio } = useMemo(() => {
    if (totalExpenses === 0) return { level: ANT_LEVELS[0], ratio: 0 }
    const r = antExpenseAmount / totalExpenses
    const lvl = ANT_LEVELS.find(l => r <= l.maxRatio) || ANT_LEVELS[ANT_LEVELS.length - 1]
    return { level: lvl, ratio: r }
  }, [antExpenseAmount, totalExpenses])

  const percentage = Math.round(ratio * 100)

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex flex-col items-center justify-between p-3 rounded-xl w-full",
          "bg-gradient-to-br from-amber-50 to-amber-100/80",
          "dark:from-amber-950/30 dark:to-amber-900/20",
          "border border-amber-200/60 dark:border-amber-700/40",
          "hover:border-amber-300 hover:shadow-md",
          "transition-all cursor-pointer active:scale-[0.98]",
          "focus:outline-none focus:ring-2 focus:ring-amber-400/50"
        )}
        data-testid="ant-indicator-btn"
      >
        {/* Top row: Icon + Label */}
        <div className="flex items-center gap-2 w-full mb-1">
          <span className={cn(compact ? level.compactSize : level.size, "leading-none")}>
            🐜
          </span>
          <span className="text-xs font-medium text-amber-700 dark:text-amber-400 truncate">
            {isSpanish ? 'Gasto hormiga' : 'Ant expense'}
          </span>
        </div>
        
        {/* Amount */}
        <div className="w-full text-left">
          <span className="text-lg font-bold text-amber-700 dark:text-amber-300">
            {formatCurrency(antExpenseAmount)}
          </span>
          <span className="text-xs text-amber-600/70 dark:text-amber-400/70 ml-1">
            ({percentage}%)
          </span>
        </div>
        
        {/* Status message */}
        <p className="text-[10px] text-amber-600/60 dark:text-amber-500/50 w-full text-left mt-1">
          {isSpanish ? level.message.es : level.message.en} · {isSpanish ? 'Ver detalles' : 'See details'}
        </p>
      </button>
    )
  }

  // Original full-size version
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-2 p-4 rounded-2xl",
        "bg-amber-500/10 border border-amber-500/30",
        "hover:bg-amber-500/20 hover:border-amber-500/50",
        "transition-all cursor-pointer active:scale-95",
        "focus:outline-none focus:ring-2 focus:ring-amber-500/50"
      )}
      data-testid="ant-indicator-btn"
    >
      {/* Ant emoji with dynamic size */}
      <span 
        className={cn(level.size, "leading-none")}
        role="img" 
        aria-label={isSpanish ? 'Hormiga' : 'Ant'}
      >
        🐜
      </span>
      
      {/* Message */}
      <p className="text-sm font-medium text-amber-700 dark:text-amber-400 text-center max-w-[200px]">
        {isSpanish ? level.message.es : level.message.en}
      </p>
      
      {/* Stats */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-bold text-amber-600 dark:text-amber-400">
          {formatCurrency(antExpenseAmount)}
        </span>
        <span>({percentage}% {isSpanish ? 'del total' : 'of total'})</span>
      </div>
      
      {/* Hint to click */}
      <span className="text-xs text-muted-foreground/70">
        {isSpanish ? 'Toca para ver detalles' : 'Tap to see details'}
      </span>
    </button>
  )
}
